import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Trash2, Loader2, Users, Eye, FileText, Clock, CheckCircle, AlertCircle, Circle, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-800', cardColor: 'border-blue-300 bg-blue-50' },
  { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800', cardColor: 'border-yellow-300 bg-yellow-50' },
  { value: 'contacted', label: 'Contacted', color: 'bg-purple-100 text-purple-800', cardColor: 'border-purple-300 bg-purple-50' },
  { value: 'declined', label: 'Declined', color: 'bg-red-100 text-red-800', cardColor: 'border-red-300 bg-red-50' },
];

export default function InquiriesManager() {
  const [inquiries, setInquiries] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('new');
  const [sortColumn, setSortColumn] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [sendingContract, setSendingContract] = useState(null);
  const [converting, setConverting] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [inquiriesRes, contractsRes] = await Promise.all([
        axios.get(`${API}/inquiries`),
        axios.get(`${API}/contracts`).catch(() => ({ data: [] }))
      ]);
      setInquiries(inquiriesRes.data);
      setContracts(contractsRes.data || []);
    } catch (error) {
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  // Status counts for info cards
  const statusCounts = useMemo(() => {
    const counts = { all: inquiries.length };
    STATUS_OPTIONS.forEach(opt => {
      counts[opt.value] = inquiries.filter(i => (i.status || 'new') === opt.value).length;
    });
    return counts;
  }, [inquiries]);

  // Get contract status for an inquiry
  const getContractStatus = (inquiryId) => {
    const inquiryContracts = contracts.filter(c => c.inquiry_id === inquiryId);
    if (inquiryContracts.length === 0) {
      return { status: 'not_sent', label: 'Send Contract', color: 'bg-gray-100 text-gray-700 hover:bg-gray-200', buttonStyle: true };
    }
    
    const signedContract = inquiryContracts.find(c => c.status === 'signed');
    if (signedContract) {
      return { status: 'signed', label: 'Contract Signed', color: 'bg-green-100 text-green-800', buttonStyle: false };
    }
    
    const pendingContract = inquiryContracts.find(c => c.status === 'pending');
    if (pendingContract) {
      return { status: 'pending', label: 'Contract Sent', color: 'bg-yellow-100 text-yellow-800', buttonStyle: false };
    }
    
    return { status: 'expired', label: 'Expired', color: 'bg-red-100 text-red-800', buttonStyle: false };
  };

  // Get inquiry status display
  const getInquiryStatusDisplay = (status) => {
    switch (status) {
      case 'new':
        return { label: 'New', color: 'bg-blue-100 text-blue-800', icon: Circle };
      case 'pending':
        return { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock };
      case 'contacted':
        return { label: 'Contacted', color: 'bg-purple-100 text-purple-800', icon: Circle };
      case 'declined':
        return { label: 'Declined', color: 'bg-red-100 text-red-800', icon: AlertCircle };
      default:
        return { label: status || 'New', color: 'bg-gray-100 text-gray-600', icon: Circle };
    }
  };

  const handleDelete = async (e, inquiry) => {
    e.stopPropagation();
    if (!window.confirm(`Delete inquiry from ${inquiry.first_name} ${inquiry.last_name}?`)) return;
    
    try {
      await axios.delete(`${API}/inquiries/${inquiry.id}`);
      toast.success('Inquiry deleted');
      setSelectedIds(prev => prev.filter(id => id !== inquiry.id));
      fetchData();
    } catch (error) {
      toast.error('Failed to delete inquiry');
    }
  };

  const handleSendContract = async (e, inquiry) => {
    e.stopPropagation();
    setSendingContract(inquiry.id);
    try {
      await axios.post(`${API}/contracts/inquiry`, { inquiry_id: inquiry.id });
      toast.success(`Contract sent to ${inquiry.email}`);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('pending')) {
        toast.error('A contract has already been sent.');
      } else if (detail.includes('signed')) {
        toast.error('Contract already signed.');
      } else {
        toast.error(detail || 'Failed to send contract');
      }
    } finally {
      setSendingContract(null);
    }
  };

  const handleConvertToRenter = async (e, inquiry) => {
    e.stopPropagation();
    if (!window.confirm(`Convert ${inquiry.first_name} ${inquiry.last_name} to a renter?`)) return;
    
    setConverting(inquiry.id);
    try {
      const response = await axios.post(`${API}/inquiries/${inquiry.id}/convert-to-renter`);
      toast.success('Converted to renter');
      navigate(`/admin/renters/${response.data.renter_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert');
    } finally {
      setConverting(null);
    }
  };

  const handleStatusChange = async (inquiryId, newStatus) => {
    setUpdatingStatus(inquiryId);
    try {
      await axios.put(`${API}/inquiries/${inquiryId}`, { status: newStatus });
      setInquiries(prev => prev.map(inq => 
        inq.id === inquiryId ? { ...inq, status: newStatus } : inq
      ));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedIds.length === 0) {
      toast.error('No inquiries selected');
      return;
    }
    
    setBulkUpdating(true);
    try {
      await Promise.all(
        selectedIds.map(id => axios.put(`${API}/inquiries/${id}`, { status: newStatus }))
      );
      setInquiries(prev => prev.map(inq => 
        selectedIds.includes(inq.id) ? { ...inq, status: newStatus } : inq
      ));
      toast.success(`Updated ${selectedIds.length} inquiries`);
      setSelectedIds([]);
    } catch (error) {
      toast.error('Failed to update some inquiries');
    } finally {
      setBulkUpdating(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  const formatLongDate = (dateString) => {
    if (!dateString) return '-';
    // Handle date-only strings (YYYY-MM-DD) to avoid timezone issues
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/Toronto'
      });
    }
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'America/Toronto'
    });
  };

  const formatCheckDate = (dateString) => {
    if (!dateString) return '-';
    // Handle date-only strings (YYYY-MM-DD) to avoid timezone issues
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/Toronto'
      });
    }
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'America/Toronto'
    });
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  // Handle selection
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredInquiries.map(i => i.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (inquiryId, checked) => {
    if (checked) {
      setSelectedIds(prev => [...prev, inquiryId]);
    } else {
      setSelectedIds(prev => prev.filter(id => id !== inquiryId));
    }
  };

  // Filter and sort inquiries
  const filteredInquiries = useMemo(() => {
    let filtered = inquiries.filter(inquiry => {
      const searchLower = search.toLowerCase();
      const matchesSearch = 
        inquiry.first_name?.toLowerCase().includes(searchLower) ||
        inquiry.last_name?.toLowerCase().includes(searchLower) ||
        inquiry.email?.toLowerCase().includes(searchLower);
      
      if (!matchesSearch) return false;

      if (statusFilter !== 'all') {
        const inquiryStatus = inquiry.status || 'new';
        if (statusFilter !== inquiryStatus) return false;
      }

      return true;
    });

    // Sort based on column and direction
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortColumn) {
        case 'name':
          comparison = `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
          break;
        case 'created_at':
          comparison = new Date(a.created_at || 0) - new Date(b.created_at || 0);
          break;
        case 'status':
          comparison = (a.status || 'new').localeCompare(b.status || 'new');
          break;
        case 'check_in_date':
          comparison = new Date(a.check_in_date || 0) - new Date(b.check_in_date || 0);
          break;
        case 'check_out_date':
          comparison = new Date(a.check_out_date || 0) - new Date(b.check_out_date || 0);
          break;
        case 'total_with_hst':
          comparison = (a.total_with_hst || 0) - (b.total_with_hst || 0);
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [inquiries, search, statusFilter, sortColumn, sortDirection]);

  const allSelected = filteredInquiries.length > 0 && selectedIds.length === filteredInquiries.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < filteredInquiries.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#1B4D3E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1B4D3E]">Inquiries</h1>
          <p className="text-gray-500">{filteredInquiries.length} of {inquiries.length} inquiries</p>
        </div>
      </div>

      {/* Status Info Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'all' ? 'ring-2 ring-[#1B4D3E] border-[#1B4D3E]' : 'border-gray-200'}`}
          onClick={() => setStatusFilter('all')}
        >
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-[#1B4D3E]">{statusCounts.all}</p>
            <p className="text-xs text-gray-600">All</p>
          </CardContent>
        </Card>
        {STATUS_OPTIONS.map(opt => (
          <Card 
            key={opt.value}
            className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === opt.value ? `ring-2 ring-[#1B4D3E] ${opt.cardColor}` : opt.cardColor}`}
            onClick={() => setStatusFilter(opt.value)}
          >
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold">{statusCounts[opt.value]}</p>
              <p className="text-xs">{opt.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search and Bulk Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        
        {/* Bulk Status Change */}
        {selectedIds.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">{selectedIds.length} selected</span>
            <Select onValueChange={handleBulkStatusChange} disabled={bulkUpdating}>
              <SelectTrigger className="w-[180px]">
                {bulkUpdating ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                <SelectValue placeholder="Change Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <span className={`px-2 py-0.5 rounded text-xs ${option.color}`}>
                      {option.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Inquiries Table */}
      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]">
                <Checkbox 
                  checked={allSelected}
                  onCheckedChange={handleSelectAll}
                  aria-label="Select all"
                  className={someSelected ? 'data-[state=checked]:bg-[#1B4D3E]' : ''}
                />
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-gray-50 select-none"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center">
                  Name {getSortIcon('name')}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-gray-50 select-none"
                onClick={() => handleSort('created_at')}
              >
                <div className="flex items-center">
                  Created {getSortIcon('created_at')}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-gray-50 select-none"
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center">
                  Status {getSortIcon('status')}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-gray-50 select-none"
                onClick={() => handleSort('check_in_date')}
              >
                <div className="flex items-center">
                  Check-in {getSortIcon('check_in_date')}
                </div>
              </TableHead>
              <TableHead 
                className="cursor-pointer hover:bg-gray-50 select-none"
                onClick={() => handleSort('check_out_date')}
              >
                <div className="flex items-center">
                  Check-out {getSortIcon('check_out_date')}
                </div>
              </TableHead>
              <TableHead 
                className="text-right cursor-pointer hover:bg-gray-50 select-none"
                onClick={() => handleSort('total_with_hst')}
              >
                <div className="flex items-center justify-end">
                  Rent Owing {getSortIcon('total_with_hst')}
                </div>
              </TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredInquiries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-gray-500">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No inquiries found</p>
                </TableCell>
              </TableRow>
            ) : (
              filteredInquiries.map((inquiry) => {
                const contractStatus = getContractStatus(inquiry.id);
                const inquiryStatus = getInquiryStatusDisplay(inquiry.status || 'new');
                const StatusIcon = inquiryStatus.icon;
                const isConverted = inquiry.status === 'converted';
                const isSelected = selectedIds.includes(inquiry.id);
                
                return (
                <TableRow 
                  key={inquiry.id} 
                  className={`cursor-pointer hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                  onClick={() => navigate(`/admin/inquiries/${inquiry.id}`)}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox 
                      checked={isSelected}
                      onCheckedChange={(checked) => handleSelectOne(inquiry.id, checked)}
                      aria-label={`Select ${inquiry.first_name}`}
                    />
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-[#1B4D3E]">
                        {inquiry.first_name} {inquiry.last_name}
                      </p>
                      <p className="text-sm text-gray-500">{inquiry.email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {formatLongDate(inquiry.created_at)}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Select
                      value={inquiry.status || 'new'}
                      onValueChange={(value) => handleStatusChange(inquiry.id, value)}
                      disabled={updatingStatus === inquiry.id}
                    >
                      <SelectTrigger className={`w-[130px] h-8 text-xs ${inquiryStatus.color} border-0`}>
                        <div className="flex items-center gap-1">
                          {updatingStatus === inquiry.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <StatusIcon className="h-3 w-3" />
                          )}
                          <SelectValue />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            <span className={`px-2 py-0.5 rounded text-xs ${option.color}`}>
                              {option.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatCheckDate(inquiry.check_in_date)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatCheckDate(inquiry.check_out_date)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(inquiry.total_with_hst)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      {/* Contract Button */}
                      {contractStatus.buttonStyle && !isConverted ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => handleSendContract(e, inquiry)}
                          disabled={sendingContract === inquiry.id}
                          className={`text-xs ${contractStatus.color}`}
                        >
                          {sendingContract === inquiry.id ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : null}
                          {contractStatus.label}
                        </Button>
                      ) : (
                        <span className={`px-2 py-1 rounded text-xs ${contractStatus.color}`}>
                          {contractStatus.label}
                        </span>
                      )}
                      {/* Convert to Renter Button */}
                      {!isConverted && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => handleConvertToRenter(e, inquiry)}
                          disabled={converting === inquiry.id}
                          className="text-xs text-green-700 border-green-300 hover:bg-green-50"
                        >
                          {converting === inquiry.id ? (
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                          ) : null}
                          Convert to Renter
                        </Button>
                      )}
                      {/* View Details */}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => navigate(`/admin/inquiries/${inquiry.id}`)}
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {/* Delete */}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => handleDelete(e, inquiry)}
                        title="Delete"
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )})
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
